import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  getCurrentUser,
  fetchAuthSession,
  signIn,
  signUp,
  confirmSignUp,
  signOut as amplifySignOut,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
} from 'aws-amplify/auth';
import { trackSignUp, trackLogin } from '@/services/analytics';

export interface AuthUser {
  readonly userId: string;
  readonly email: string;
  readonly groups: readonly string[];
}

interface AuthState {
  readonly user: AuthUser | null;
  readonly isLoading: boolean;
  readonly isAuthenticated: boolean;
  readonly isAdmin: boolean;
}

interface AuthActions {
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly signUp: (email: string, password: string, name: string) => Promise<void>;
  readonly confirmSignUp: (email: string, code: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly refreshUser: () => Promise<void>;
}

export type AuthContextValue = AuthState & AuthActions;

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  readonly children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const [currentUser, session] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession({ forceRefresh: true }),
      ]);
      const idToken = session.tokens?.idToken;
      const groups = (idToken?.payload?.['cognito:groups'] as string[] | undefined) ?? [];

      return {
        userId: currentUser.userId,
        email: currentUser.signInDetails?.loginId ?? '',
        groups,
      };
    } catch {
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const authUser = await fetchUser();
      setUser(authUser);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUser]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const handleSignIn = useCallback(
    async (email: string, password: string) => {
      await signIn({ username: email, password } satisfies SignInInput);
      await refreshUser();
      trackLogin('email');
    },
    [refreshUser],
  );

  const handleSignUp = useCallback(
    async (email: string, password: string, name: string) => {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email, name },
        },
      } satisfies SignUpInput);
    },
    [],
  );

  const handleConfirmSignUp = useCallback(
    async (email: string, code: string) => {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      } satisfies ConfirmSignUpInput);
      trackSignUp('email');
    },
    [],
  );

  const handleSignOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    isAdmin: user?.groups.includes('admin') ?? false,
    signIn: handleSignIn,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    signOut: handleSignOut,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
